import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Camera, Upload, Check, Clock, X, ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

// Import default avatars
import defaultAvatar1 from '@/assets/avatars/default-1.png';
import defaultAvatar2 from '@/assets/avatars/default-2.png';
import defaultAvatar3 from '@/assets/avatars/default-3.png';
import defaultAvatar4 from '@/assets/avatars/default-4.png';
import defaultAvatar5 from '@/assets/avatars/default-5.png';
import defaultAvatar6 from '@/assets/avatars/default-6.png';
import defaultAvatar7 from '@/assets/avatars/default-7.png';
import defaultAvatar8 from '@/assets/avatars/default-8.png';
import defaultAvatar9 from '@/assets/avatars/default-9.png';
import defaultAvatar10 from '@/assets/avatars/default-10.png';

const DEFAULT_AVATARS = [
  defaultAvatar1,
  defaultAvatar2,
  defaultAvatar3,
  defaultAvatar4,
  defaultAvatar5,
  defaultAvatar6,
  defaultAvatar7,
  defaultAvatar8,
  defaultAvatar9,
  defaultAvatar10,
];

interface ProfileAvatarSectionProps {
  userId: string;
  currentAvatarUrl: string | null;
  currentDefaultAvatar: number | null;
  onAvatarChange: () => void;
}

interface AvatarRequest {
  id: string;
  file_path: string;
  status: 'pending' | 'approved' | 'rejected';
  admin_note: string | null;
  created_at: string;
}

export function ProfileAvatarSection({ 
  userId, 
  currentAvatarUrl, 
  currentDefaultAvatar,
  onAvatarChange 
}: ProfileAvatarSectionProps) {
  const [uploading, setUploading] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);
  const [avatarRequest, setAvatarRequest] = useState<AvatarRequest | null>(null);
  const [selectedDefault, setSelectedDefault] = useState<number | null>(currentDefaultAvatar);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchAvatarRequest();
  }, [userId]);

  useEffect(() => {
    setSelectedDefault(currentDefaultAvatar);
  }, [currentDefaultAvatar]);

  const fetchAvatarRequest = async () => {
    const { data, error } = await supabase
      .from('profile_avatars')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (!error && data) {
      setAvatarRequest(data as AvatarRequest);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Endast bildfiler är tillåtna');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Bilden får inte vara större än 2MB');
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/${Date.now()}.${fileExt}`;

      // Delete old file if exists
      if (avatarRequest?.file_path) {
        await supabase.storage.from('avatars').remove([avatarRequest.file_path]);
      }

      // Upload new file
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Create or update avatar request
      if (avatarRequest) {
        const { error: updateError } = await supabase
          .from('profile_avatars')
          .update({
            file_path: fileName,
            status: 'pending',
            admin_note: null,
            reviewed_at: null,
            reviewed_by: null,
          })
          .eq('id', avatarRequest.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('profile_avatars')
          .insert({
            user_id: userId,
            file_path: fileName,
            status: 'pending',
          });

        if (insertError) throw insertError;
      }

      // Clear default avatar selection when uploading custom
      await supabase
        .from('profiles')
        .update({ default_avatar: null })
        .eq('id', userId);

      toast.success('Bild uppladdad! Den kommer granskas av en administratör.');
      fetchAvatarRequest();
      onAvatarChange();
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error('Kunde inte ladda upp bilden: ' + error.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSelectDefaultAvatar = async (index: number) => {
    setSavingDefault(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          default_avatar: index,
          avatar_url: null // Clear custom avatar when selecting default
        })
        .eq('id', userId);

      if (error) throw error;

      setSelectedDefault(index);
      toast.success('Standardbild vald!');
      onAvatarChange();
    } catch (error: any) {
      console.error('Error selecting default avatar:', error);
      toast.error('Kunde inte välja standardbild');
    } finally {
      setSavingDefault(false);
    }
  };

  const getAvatarUrl = (filePath: string) => {
    const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);
    return data.publicUrl;
  };

  const getCurrentDisplayAvatar = () => {
    if (currentAvatarUrl) {
      return currentAvatarUrl;
    }
    if (avatarRequest?.status === 'approved') {
      return getAvatarUrl(avatarRequest.file_path);
    }
    if (selectedDefault !== null && selectedDefault >= 0 && selectedDefault < DEFAULT_AVATARS.length) {
      return DEFAULT_AVATARS[selectedDefault];
    }
    return DEFAULT_AVATARS[0];
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Camera className="h-5 w-5 text-primary" />
          <CardTitle className="font-display text-lg">Profilbild</CardTitle>
        </div>
        <CardDescription>
          Ladda upp en egen bild eller välj en standardbild
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Avatar Display */}
        <div className="flex items-center gap-4">
          <div className="relative">
            <img
              src={getCurrentDisplayAvatar()}
              alt="Din profilbild"
              className="w-20 h-20 rounded-full object-cover border-2 border-border"
            />
            {avatarRequest?.status === 'pending' && (
              <div className="absolute -bottom-1 -right-1 bg-warning text-warning-foreground rounded-full p-1">
                <Clock className="h-4 w-4" />
              </div>
            )}
            {avatarRequest?.status === 'approved' && currentAvatarUrl && (
              <div className="absolute -bottom-1 -right-1 bg-green-500 text-white rounded-full p-1">
                <Check className="h-4 w-4" />
              </div>
            )}
          </div>
          <div className="flex-1">
            {avatarRequest?.status === 'pending' && (
              <div className="flex items-center gap-2 text-sm text-warning">
                <Clock className="h-4 w-4" />
                <span>Din bild väntar på granskning</span>
              </div>
            )}
            {avatarRequest?.status === 'rejected' && (
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <X className="h-4 w-4" />
                  <span>Din bild nekades</span>
                </div>
                {avatarRequest.admin_note && (
                  <p className="text-sm text-muted-foreground">
                    Anledning: {avatarRequest.admin_note}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Upload Custom Avatar */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Ladda upp egen bild</p>
          <p className="text-xs text-muted-foreground">
            Max 2MB, bilden granskas innan den publiceras
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            {uploading ? 'Laddar upp...' : 'Välj bild'}
          </Button>
        </div>

        {/* Default Avatars */}
        <div className="space-y-3">
          <p className="text-sm font-medium">Eller välj en standardbild</p>
          <div className="grid grid-cols-5 gap-2">
            {DEFAULT_AVATARS.map((avatar, index) => (
              <button
                key={index}
                onClick={() => handleSelectDefaultAvatar(index)}
                disabled={savingDefault}
                className={cn(
                  "relative rounded-full overflow-hidden border-2 transition-all hover:scale-105",
                  selectedDefault === index && !currentAvatarUrl
                    ? "border-primary ring-2 ring-primary/20"
                    : "border-transparent hover:border-primary/50"
                )}
              >
                <img
                  src={avatar}
                  alt={`Standardbild ${index + 1}`}
                  className="w-full aspect-square object-cover"
                />
                {selectedDefault === index && !currentAvatarUrl && (
                  <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                    <Check className="h-4 w-4 text-primary" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Helper function to get avatar for display in other components
export function getDisplayAvatar(avatarUrl: string | null, defaultAvatar: number | null): string {
  if (avatarUrl) {
    return avatarUrl;
  }
  if (defaultAvatar !== null && defaultAvatar >= 0 && defaultAvatar < DEFAULT_AVATARS.length) {
    return DEFAULT_AVATARS[defaultAvatar];
  }
  return DEFAULT_AVATARS[0];
}

export { DEFAULT_AVATARS };
